import { z } from 'zod';
import { idSchema, unixMsSchema } from './common.ts';

export const screenGroupSchema = z.object({
  id: idSchema,
  org_id: idSchema,
  name: z.string().min(1),
  screen_ids: z.array(idSchema),
  // Group-level overlay (Sprint 4). Null = no group overlay. Resolution order in the
  // manifest: screen.overlay_id ?? first-by-name group overlay ?? org default.
  overlay_id: idSchema.nullable(),
  // Sprint 9: explicit "no overlay" opt-out for this group. When the first-by-name
  // group (docs/02 resolution order) has this set, resolution stops at NONE — the org
  // default is NOT applied. Mutually exclusive with overlay_id (service-enforced):
  // assigning one clears the other; a PATCH that sets a real overlay_id AND
  // overlay_opt_out:true in the same request is rejected (422), not silently resolved.
  overlay_opt_out: z.boolean(),
  created_at: unixMsSchema,
});
export type ScreenGroup = z.infer<typeof screenGroupSchema>;

export const createGroupSchema = z.object({
  name: z.string().min(1),
});
export type CreateGroup = z.infer<typeof createGroupSchema>;

// PATCH /v1/groups/:id — rename and/or (re)assign the group overlay. An explicit null
// clears the group overlay; omitting overlay_id leaves it unchanged. overlay_opt_out
// and overlay_id are mutually exclusive (service-enforced): assigning a real overlay_id
// clears overlay_opt_out and vice versa; submitting a real overlay_id together with
// overlay_opt_out:true in the same request is a 422 (contradiction, not a precedence rule).
export const updateGroupSchema = z.object({
  name: z.string().min(1).optional(),
  overlay_id: idSchema.nullable().optional(),
  overlay_opt_out: z.boolean().optional(),
});
export type UpdateGroup = z.infer<typeof updateGroupSchema>;

// PUT /v1/groups/:id/members — full replace of membership.
export const setGroupMembersSchema = z.object({
  screen_ids: z.array(idSchema),
});
export type SetGroupMembers = z.infer<typeof setGroupMembersSchema>;
